import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import useStore from '../../store/useStore';
import CategorySidebar from '../../components/Products/CategorySidebar';
import ProductCard from '../../components/Products/ProductCard';
import Loader from '../../components/Loader';
import SortDropdown from '../../components/Products/SortDropdown';
import { FaSearch, FaLeaf } from 'react-icons/fa';
import { getActivePincode } from '../../utils/location';

const ProductsPage = () => {
  const [searchParams] = useSearchParams();
  const { categoryId } = useParams();

  // Store selectors
  const getFilteredProducts  = useStore((s) => s.getFilteredProducts);
  const fetchProducts        = useStore((s) => s.fetchProducts);
  const resetProducts        = useStore((s) => s.resetProducts);
  const setSelectedCategory  = useStore((s) => s.setSelectedCategory);
  const setSearchQuery       = useStore((s) => s.setSearchQuery);
  const fetchCategories      = useStore((s) => s.fetchCategories);
  const products_loading     = useStore((s) => s.products_loading);
  const products_loaded      = useStore((s) => s.products_loaded);
  const activeCategorySlug   = useStore((s) => s.activeCategorySlug);
  const fetchCart            = useStore((s) => s.fetchCart);
  const categories           = useStore((s) => s.categories);
  const searchResults        = useStore((s) => s.searchResults);
  const searchLoading        = useStore((s) => s.searchLoading);
  const searchProducts       = useStore((s) => s.searchProducts);
  const activePincode        = useStore((s) => getActivePincode(s));



  // Local UI state
  const [fetching, setFetching]                 = useState(true);
  const [sortBy, setSortBy]                     = useState('default');
  const [priceRange, setPriceRange]             = useState([0, 1000]);
  const [discountFilter, setDiscountFilter]     = useState(0);
  const [visibleCount, setVisibleCount]         = useState(12);
  const [isFetchingMore, setIsFetchingMore]     = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const sentinelRef = useRef(null);

  // ─── Resolve URL params directly ─────────────────────────────────────────────
  // The URL is the SINGLE source of truth for which category is active.
  const rawCategoryParam = searchParams.get('category') || categoryId || 'all';
  const matchedCategory = categories.find(
    (c) => String(c.category_id) === String(rawCategoryParam) || c.slug === rawCategoryParam
  );
  const categorySlug = matchedCategory ? matchedCategory.slug : rawCategoryParam;
  const targetSlug = categorySlug !== 'all' ? categorySlug : null;
  const searchQ = searchParams.get('search') ?? searchParams.get('q') ?? '';

  // In search mode the page shows server-side search results (no category filtering)
  const isSearchMode = searchQ.trim().length >= 2;

  // ─── One-time init ───────────────────────────────────────────────────────────
  useEffect(() => {
    fetchCategories();
    if (typeof fetchCart === 'function') fetchCart();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Synchronous pre-paint reset ─────────────────────────────────────────────
  // useLayoutEffect fires synchronously BEFORE the browser paints on mount or slug change.
  // Resets store products and marks category loading immediately so stale products never flash.
  useLayoutEffect(() => {
    if (typeof resetProducts === 'function') {
      resetProducts(targetSlug);
    }
  }, [targetSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Determine if currently loading ──────────────────────────────────────────
  const isSlugMismatch = activeCategorySlug !== targetSlug;
  // In search mode: loading = searchLoading || fetching
  // In category mode: loading = fetching || products_loading || !products_loaded || isSlugMismatch
  const isLoading = isSearchMode
    ? (searchLoading || fetching)
    : (fetching || products_loading || !products_loaded || isSlugMismatch);

  // ─── Search-mode: call the search API for accurate results ──────────────────
  useEffect(() => {
    if (!isSearchMode) return;

    setSearchQuery(searchQ);
    setSelectedCategory('all');
    setVisibleCount(12);
    setSortBy('default');
    setDiscountFilter(0);
    setPriceRange([0, 1000]);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Use the same search API as the header dropdown — accurate server-side results
    setFetching(true);
    searchProducts(searchQ, 100).finally(() => {
      setFetching(false);
    });
  }, [searchQ, activePincode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Category-mode: fetch products for this category ─────────────────────────
  useEffect(() => {
    if (isSearchMode) return; // skip when in search mode

    // Sync Zustand selectedCategory state
    setSelectedCategory(categorySlug);
    setSearchQuery('');

    // Reset local pagination & filters on every category switch
    setSortBy('default');
    setDiscountFilter(0);
    setVisibleCount(12);
    setPriceRange([0, 1000]);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Fetch products for this specific category from API
    setFetching(true);
    fetchProducts(targetSlug, { pincode: activePincode }).finally(() => {
      setFetching(false);
    });
  }, [targetSlug, isSearchMode, activePincode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset pagination when local filters change
  useEffect(() => {
    setVisibleCount(12);
  }, [discountFilter, priceRange]);

  // ─── Filtering & sorting ─────────────────────────────────────────────────────
  // In search mode, use the search API results (normalized) directly.
  // In category mode, use the store's filtered products as before.
  const products = isSearchMode
    ? searchResults  // already normalized by searchSlice
    : (isSlugMismatch ? [] : getFilteredProducts());

  const getDisplayedDiscount = (p) => {
    const firstVariant = Array.isArray(p.variants) ? p.variants[0] : null;
    const basePrice = Number.parseFloat(p.price ?? 0);
    const baseOrig  = Number.parseFloat(p.originalPrice ?? p.original_price ?? basePrice);
    const selPrice  = Number.parseFloat(firstVariant?.discount_price ?? firstVariant?.price ?? basePrice);
    const selOrig   = Number.parseFloat(firstVariant?.original_price ?? firstVariant?.originalPrice ?? baseOrig);
    if (selOrig > selPrice && selOrig > 0) {
      return Math.round(((selOrig - selPrice) / selOrig) * 100);
    }
    return 0;
  };

  const filteredProducts = products.filter(
    p =>
      p.price >= priceRange[0] &&
      p.price <= priceRange[1] &&
      (discountFilter === 0 || getDisplayedDiscount(p) >= discountFilter)
  );

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === 'price-low')  return a.price - b.price;
    if (sortBy === 'price-high') return b.price - a.price;
    if (sortBy === 'discount')   return b.discount - a.discount;
    if (sortBy === 'name')       return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'default' && discountFilter > 0) {
      return getDisplayedDiscount(a) - getDisplayedDiscount(b);
    }
    return 0;
  });

  const visibleProducts = sortedProducts.slice(0, visibleCount);
  const hasMore = visibleCount < sortedProducts.length;

  // Find the current category display name for the UI
  const activeCategoryName = isSearchMode
    ? `Results for "${searchQ}"`
    : categorySlug === 'all'
      ? 'All Products'
      : (matchedCategory?.name || categories.find(c => c.slug === categorySlug)?.name || categorySlug);

  // ─── Infinite scroll ─────────────────────────────────────────────────────────
  const handleLoadMore = useCallback(() => {
    if (isFetchingMore || !hasMore) return;
    setIsFetchingMore(true);
    setTimeout(() => {
      setVisibleCount(prev => prev + 12);
      setIsFetchingMore(false);
    }, 600);
  }, [isFetchingMore, hasMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) handleLoadMore(); },
      { rootMargin: '100px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleLoadMore]);

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">

        {/* Mobile top bar */}
        <div className="mb-4 rounded-2xl bg-white p-3 shadow-sm sm:hidden">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileFiltersOpen(prev => !prev)}
              className="flex min-w-[108px] items-center justify-center gap-2 rounded-xl border border-[#d6e3d7] bg-[#f4f8f4] px-3 py-2.5 text-sm font-semibold text-[#245d3f]"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M6 12h12m-9 8h6" />
              </svg>
              Filters
            </button>
            <div className="min-w-0 flex-1">
              <SortDropdown value={sortBy} onChange={setSortBy} />
            </div>
          </div>
          <div className="mt-2.5 flex items-center justify-between">
            <span className="text-sm font-bold text-[#2E7B52] truncate max-w-[60%]">
              {activeCategoryName}
            </span>
            <span className="text-xs text-gray-500">
              {isLoading
                ? 'Loading…'
                : `${sortedProducts.length} product${sortedProducts.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        </div>

        {mobileFiltersOpen && (
          <div className="mb-5 sm:hidden">
            <CategorySidebar
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              discountFilter={discountFilter}
              setDiscountFilter={setDiscountFilter}
              mobileMode
              onClose={() => setMobileFiltersOpen(false)}
            />
          </div>
        )}

        <div className="flex flex-col gap-8 lg:flex-row">
          {/* Sidebar */}
          <aside className="hidden lg:block lg:w-1/4">
            <CategorySidebar
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              discountFilter={discountFilter}
              setDiscountFilter={setDiscountFilter}
            />
          </aside>

          {/* Main content */}
          <main className="lg:w-3/4">
            {/* Desktop sort bar */}
            <div className="mb-6 hidden rounded-xl bg-white p-4 shadow-sm sm:block">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#2E7B52]">
                    {activeCategoryName}
                  </p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {isLoading
                      ? 'Loading products…'
                      : `${sortedProducts.length} product${sortedProducts.length !== 1 ? 's' : ''} found`}
                  </p>
                </div>
                <SortDropdown value={sortBy} onChange={setSortBy} />
              </div>
            </div>

            {/* ── Loading state ── */}
            {isLoading ? (
              <div className="rounded-2xl bg-white shadow-sm">
                <Loader
                  label="Loading products"
                  sublabel={`Fetching products${categorySlug !== 'all' ? ` for "${activeCategoryName}"` : ''}…`}
                />
              </div>

            /* ── Empty state ── */
            ) : sortedProducts.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <div className="mx-auto mb-5 flex h-24 w-24 items-center justify-center rounded-full bg-green-50">
                  <FaLeaf className="h-10 w-10 text-[#2E7B52] opacity-50" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">No products found</h3>
                <p className="text-gray-500 text-sm max-w-xs mx-auto">
                  {categorySlug !== 'all'
                    ? `There are no products available in "${activeCategoryName}" right now.`
                    : 'No products match your current filters. Try adjusting them.'}
                </p>
                {(discountFilter > 0 || priceRange[1] < 1000) && (
                  <button
                    onClick={() => { setDiscountFilter(0); setPriceRange([0, 1000]); }}
                    className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#2E7B52] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#245d3f] transition-colors"
                  >
                    <FaSearch className="h-3.5 w-3.5" />
                    Clear Filters
                  </button>
                )}
              </div>

            /* ── Products grid ── */
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
                  {visibleProducts.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {/* Infinite scroll sentinel */}
                {hasMore && (
                  <div ref={sentinelRef} className="mt-10 flex flex-col items-center gap-3 py-8">
                    <div className="relative flex items-center justify-center">
                      <div className="h-10 w-10 rounded-full border-4 border-gray-100 border-t-green-500 animate-spin" />
                    </div>
                    <p className="text-sm font-medium text-gray-400 tracking-wide">
                      Loading more products…
                    </p>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
